// src/components/common/Tooltip.js
import React, { useState } from 'react';
import './Tooltip.css';

/**
 * Tooltip - Reusable tooltip component for showing helpful text on hover
 *
 * Displays a small popup with helpful text when hovering over the wrapped element.
 * Supports multiple positions and auto-hides on mobile to avoid UI clutter.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - The element to wrap with tooltip (usually a button)
 * @param {string} props.text - Tooltip text to display
 * @param {string} props.position - Position relative to element: 'top', 'bottom', 'left', 'right' (default: 'top')
 * @param {number} props.delay - Delay in ms before showing tooltip (default: 300)
 * @param {string} props.className - Optional additional CSS classes
 *
 * @example
 * <Tooltip text="Delete this item" position="top">
 *   <button aria-label="Delete">
 *     <FaTrash />
 *   </button>
 * </Tooltip>
 */
const Tooltip = ({
  children,
  text,
  position = 'top',
  delay = 300,
  className = ''
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [timeoutId, setTimeoutId] = useState(null);

  const handleMouseEnter = () => {
    // Clear any existing timeout
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    // Set new timeout to show tooltip after delay
    const id = setTimeout(() => {
      setIsVisible(true);
    }, delay);

    setTimeoutId(id);
  };

  const handleMouseLeave = () => {
    // Clear timeout if mouse leaves before tooltip shows
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setIsVisible(false);
  };

  // Don't render tooltip if no text provided
  if (!text) {
    return children;
  }

  return (
    <div
      className={`tooltip-wrapper ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
    >
      {children}
      {isVisible && (
        <div
          className={`tooltip tooltip--${position}`}
          role="tooltip"
          aria-hidden="true"
        >
          {text}
        </div>
      )}
    </div>
  );
};

export default Tooltip;
